import {CompanyData, CompanyRequest, useCompany, useUpdateCompany, useCreateCompany, useGetImage} from '@api'
import {FormFieldType, ImageType, NavigationPathKey, TranslationKey} from '@enums'
import {errorAtom, isFormDirtyAtom, handleNavigateAtom, pageLinksAtom, getUserSelectionAtom} from '@state'

import {useEffect, useState} from 'react'

import {useUploadImage} from 'api/endpoints/image/mutations'
import {
  countries,
  FormFieldItem,
  generateReadableErrorMessages,
  getUserData,
  useViewNavigate,
  validateFields,
} from 'common'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

/**
 * Initial data
 */
const initialCompanyData: CompanyData = {
  name: '',
  description: '',
  note: '',
  image: '',
  active: true,
  country: null,
  parentCompanyId: '',
}

export function useCompanyFormModel() {
  const setError = useSetAtom(errorAtom)
  const setIsFormDirty = useSetAtom(isFormDirtyAtom)
  const handleNavigate = useSetAtom(handleNavigateAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const [companyData, setCompanyData] = useState<CompanyData>(initialCompanyData)
  const [imageFile, setImageFile] = useState<undefined | File>(undefined)
  const [companyFormFieldsErrors, setCompanyFormFieldsErrors] = useState<string[]>([])
  const navigate = useViewNavigate()
  const {id} = useParams()
  const {t} = useTranslation()

  const {data: companyResponse} = useCompany(id)
  const {mutate: updateCompanyMutation} = useUpdateCompany()
  const {mutate: createCompanyMutation} = useCreateCompany()
  const {mutate: postImageMutation} = useUploadImage()
  const {data: image} = useGetImage(ImageType.COMPANY, id)

  useEffect(() => {
    if (companyResponse && id)
      setCompanyData({
        id: companyResponse.id,
        name: companyResponse.name,
        active: companyResponse.active,
        parentCompanyId: companyResponse.parentCompanyId,
        country: companyResponse.country,
        description: companyResponse.description,
        note: companyResponse.note,
        image: image || '',
      })
    else {
      setCompanyData(prev => ({...prev, parentCompanyId: userSelection.parentCompanyId}))
    }
  }, [companyResponse, userSelection, id])

  setTitleLinks([
    {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
    {title: t(TranslationKey.Resources), link: NavigationPathKey.RESOURCES_DASHBOARD},
    {
      title: t(TranslationKey['Company search']),
      link: NavigationPathKey.COMPANY_SEARCH,
    },
    {title: t(TranslationKey['Edit partner'])},
  ])

  /**
   * A function handling Company form fields change
   * @param event Change event
   * @param checked Checked state for checkbox form fields
   */
  const onChangeCompanyGeneralFormField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    checked?: boolean,
  ) => {
    setIsFormDirty(true)
    const fieldName = event.target.name
    const value = checked ?? event.target.value
    setCompanyFormFieldsErrors(prev => prev.filter(item => item !== fieldName))
    setCompanyData(prev => ({...prev, [fieldName]: value}))
  }

  /**
   * Company form fields configuration - general section
   */
  const companyGeneralFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Company name']),
      name: 'name',
      onChange: onChangeCompanyGeneralFormField,
      value: companyData.name,
      error: companyFormFieldsErrors.includes('name'),
    },

    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Description),
      name: 'description',
      onChange: onChangeCompanyGeneralFormField,
      value: companyData.description,
      error: companyFormFieldsErrors.includes('description'),
    },
    {
      fieldType: FormFieldType.DROPDOWN,
      label: t(TranslationKey.Country),
      name: 'country',
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChangeCompanyGeneralFormField(event),
      value: companyData.country,
      items: countries(t).map(item => ({label: item.name, value: item.isoCode})),
      error: companyFormFieldsErrors.includes('country'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Note),
      name: 'note',
      onChange: onChangeCompanyGeneralFormField,
      value: companyData.note,
      error: companyFormFieldsErrors.includes('note'),
      multiline: true,
      rows: 2,
      customStyle: {height: 'unset'},
    },
    {
      fieldType: FormFieldType.CHECKBOX_GROUP,
      label: t(TranslationKey.Active),
      name: 'active',
      itemsInColumn: 1,
      checkboxes: [
        {
          checked: companyData.active,
          label: t(TranslationKey.Active),
          value: companyData.active,
          name: 'active',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeCompanyGeneralFormField(event, checked),
        },
      ],
    },
  ]

  /**
   * A custom validation function for Company form data
   * @returns If there are any invalid fields
   */
  const validateData = (): boolean => {
    const notRequiredFields: string[] = ['image', 'note', 'description']

    const invalidValues = validateFields(companyData).filter(item => !notRequiredFields.includes(item))

    const isFormInvalid = invalidValues.length > 0
    if (isFormInvalid) {
      setError({
        message: generateReadableErrorMessages(t, invalidValues),
        name: '',
      })

      setCompanyFormFieldsErrors(invalidValues)
    }
    return isFormInvalid
  }

  /**
   * A function handling the Image upload
   * @param acceptedFiles Image file
   */
  const handleDrop = (acceptedFiles: File[]): void => {
    setImageFile(acceptedFiles[0])
    setCompanyData(prev => ({...prev, image: URL.createObjectURL(acceptedFiles[0])}))
  }

  /**
   * A function handling the Save button click
   * If form is valid updates or saves a new Company
   * @returns void
   */
  const onSubmit = () => {
    if (validateData()) return

    const clearAndExit = (id: string) => {
      setIsFormDirty(false)
      navigate(`${NavigationPathKey.COMPANY_VIEW}/${id}`)
    }

    const handleSuccess = (companyId: string) => {
      if (imageFile) {
        postImageMutation(
          {
            id: companyId,
            file: imageFile,
            type: ImageType.COMPANY,
          },
          {onSuccess: () => clearAndExit(companyId)},
        )
      } else {
        clearAndExit(companyId)
      }
    }

    if (id) {
      updateCompanyMutation(
        {
          id,
          body: companyData as CompanyRequest,
        },
        {
          onSuccess: () => {
            handleSuccess(id)
          },
        },
      )
    } else {
      createCompanyMutation(companyData as CompanyRequest, {
        onSuccess: ({id}) => {
          handleSuccess(id)
        },
      })
    }
  }

  /**
   * A function handling the Discard button click
   */
  const onDiscard = (): void => {
    handleNavigate(() => navigate(-1))
  }

  return {
    onSubmit,
    handleDrop,
    onDiscard,
    t,
    image,
    companyGeneralFormFields,
  }
}
